import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const Container = styled.div`
  max-width: 500px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  margin-bottom: ${props => props.$marginBottom || '0'};
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Button = styled.button`
  background-color: #FF1493;
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  
  &:hover {
    background-color: #ff0080;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 1rem;
  text-align: center;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  margin-top: 0.2rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #666;

  a {
    color: #2196f3;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignInLink = styled.div`
  margin-top: 2rem;
  text-align: center;
  
  a {
    color: #2196f3;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InstagramInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding-left: 0.5rem;
  margin-bottom: 1rem;
`;

const AtSymbol = styled.span`
  color: #555;
  font-weight: bold;
`;

const InstagramInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: none;
  font-size: 1rem;
  outline: none;
`;

const SignUpScreen = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isArtist, setIsArtist] = useState(false);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [websiteURL, setWebsiteURL] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already signed in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (isArtist && (!instagramHandle.trim() && !websiteURL.trim())) {
      setError('Artists must provide either an Instagram handle or website URL');
      return;
    }

    setLoading(true);

    try {
      // Create the user in Firebase Auth
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        firstName: firstName,
        lastName: lastName,
        displayName: `${firstName} ${lastName}`,
        isArtist: false,
        pendingOccupation: isArtist ? "artist" : null,
        instagramHandle: isArtist ? instagramHandle.replace(/^@/, '') : null,
        websiteURL: isArtist ? websiteURL : null,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        bio: '',
      });

      // Alert if artist registration
      if (isArtist) {
        alert("Thank you for registering as an artist! Your artist status is pending review by our team. You'll receive an email when your artist account is approved.");
      }

      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error signing up:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>Sign Up</Title>
      <Subtitle>Welcome to the future of buying and selling art</Subtitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Form onSubmit={handleSignUp}>
        <InputRow>
          <Input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          
          <Input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </InputRow>
        
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          $marginBottom="1rem"
        />
        
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          $marginBottom="1rem"
        />
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="artist-checkbox"
            checked={isArtist}
            onChange={() => setIsArtist(!isArtist)}
          />
          <CheckboxLabel htmlFor="artist-checkbox">
            I am an artist who would like to display work on ArtClvb
          </CheckboxLabel>
        </CheckboxContainer>
        
        {isArtist && (
          <>
            <InstagramInputContainer>
              <AtSymbol>@</AtSymbol>
              <InstagramInput
                type="text"
                placeholder="Instagram handle"
                value={instagramHandle}
                onChange={(e) => setInstagramHandle(e.target.value.replace(/^@/, ''))}
              />
            </InstagramInputContainer>
            
            <Input
              type="url"
              placeholder="Website URL"
              value={websiteURL}
              onChange={(e) => setWebsiteURL(e.target.value)}
              $marginBottom="1rem"
            />
          </>
        )}
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="terms-checkbox"
            checked={agreeToTerms}
            onChange={() => setAgreeToTerms(!agreeToTerms)}
            required
          />
          <CheckboxLabel htmlFor="terms-checkbox">
            I agree to ArtClvb's{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </CheckboxLabel>
        </CheckboxContainer>
        
        <Button type="submit" disabled={loading}>
          {loading ? 'Creating account...' : 'Sign Up'}
        </Button>
      </Form>
      
      <SignInLink>
        Already have an account? <Link to="/signin">Sign In</Link>
      </SignInLink>
    </Container>
  );
};

export default SignUpScreen;
